import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { InventoryItem } from '../../core/models/inventory.models';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { AuthService } from '../auth/auth.service';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  defaultBranchFilterId,
  isBranchFilterLocked,
  matchesBranchFilter,
  resetBranchFilterId,
} from '../../core/utils/branch-scope.util';
import { currentTimestamp, formatCurrency } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  parseNonNegativeNumber,
  requiredText,
} from '../../core/utils/stock-qty.util';

@Component({
  selector: 'app-item-master',
  imports: [FormsModule, RouterLink],
  standalone: true,
  templateUrl: './item-master.component.html',
  styleUrl: './item-master.component.css',
})
export class ItemMasterComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly categories = this.settings.allCategories;
  readonly units = this.settings.allUnits;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));
  readonly allItems = this.inventory.allItems;

  // ============================================
  // STATE
  // ============================================
  readonly search = signal('');
  readonly categoryFilter = signal('all');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');
  readonly statusFilter = signal('all');
  readonly rentableFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 10;

  readonly showItemModal = signal(false);
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');
  readonly editingId = signal<string | null>(null);
  readonly deletingId = signal<string | null>(null);

  readonly isEditing = computed(() => this.editingId() !== null);

  readonly newItem = signal({
    code: '',
    name: '',
    category: '',
    unit: '',
    branch: '',
    location: '',
    stockLevel: 0,
    reorderLevel: 0,
    price: 0,
    status: 'active' as InventoryItem['status'],
    icon: 'bi-box-seam',
    isRentable: false,
  });

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilterId(this.auth, branches));
    });
  }

  readonly stats = computed(() => {
    const items = this.allItems();
    const totalItems = items.length;
    const activeItems = items.filter((i) => i.status === 'active').length;
    const lowStock = items.filter(
      (i) => i.stockLevel <= i.reorderLevel && i.status === 'active',
    ).length;
    const discontinued = items.filter(
      (i) => i.status === 'discontinued',
    ).length;
    const rentableItems = items.filter((i) => i.isRentable === true).length;
    return { totalItems, activeItems, lowStock, discontinued, rentableItems };
  });

  readonly filteredItems = computed(() => {
    const search = this.search().trim().toLowerCase();
    const category = this.categoryFilter();
    const branch = this.branchFilter();
    const location = this.locationFilter();
    const status = this.statusFilter();
    const rentable = this.rentableFilter();

    return this.allItems().filter((item) => {
      const matchesSearch =
        !search ||
        item.code.toLowerCase().includes(search) ||
        item.name.toLowerCase().includes(search) ||
        item.category.toLowerCase().includes(search) ||
        (item.branch && item.branch.toLowerCase().includes(search)) ||
        (item.location && item.location.toLowerCase().includes(search));
      const matchesCategory =
        category === 'all' ||
        item.category === category ||
        item.categoryId === category ||
        this.categories().find((row) => row.id === category)?.name ===
          item.category;
      const matchesBranch =
        branch === 'all' ||
        matchesBranchFilter(
          branch,
          item.branch,
          item.branchId,
          this.branches(),
        );
      const matchesLocation =
        location === 'all' ||
        item.location === location ||
        item.locationId === location ||
        this.locations().find((row) => row.id === location)?.name ===
          item.location;
      const matchesStatus = status === 'all' || item.status === status;
      const matchesRentable =
        rentable === 'all' ||
        (rentable === 'true' && item.isRentable === true) ||
        (rentable === 'false' && item.isRentable === false);
      return (
        matchesSearch &&
        matchesCategory &&
        matchesBranch &&
        matchesLocation &&
        matchesStatus &&
        matchesRentable
      );
    });
  });

  readonly paginatedItems = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredItems().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredItems().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredItems().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredItems().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  readonly modalLocations = computed(() => {
    const branchId = this.newItem().branch;
    if (!branchId) {
      return this.locations();
    }
    return this.locations().filter(
      (loc) => !loc.branchId || loc.branchId === branchId,
    );
  });

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe();
    }
  }

  stockLevelClass(item: any): string {
    if (item.stockLevel === 0) return 'out';
    if (item.stockLevel <= item.reorderLevel) return 'low';
    return 'ok';
  }

  statusLabel(status: InventoryItem['status']): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  updateNewItem(
    field: keyof ReturnType<typeof this.newItem>,
    value: string | number | boolean,
  ): void {
    if (field === 'stockLevel' || field === 'reorderLevel') {
      const parsed = parseNonNegativeNumber(
        value,
        field === 'stockLevel' ? 'Stock level' : 'Reorder level',
      );
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid number',
          'error',
        );
        return;
      }
      value = parsed.value;
    }
    if (field === 'price') {
      const parsed = parseNonNegativeNumber(value, 'Price');
      if (!parsed.ok) {
        this.showToastMessage(parsed.message ?? 'Enter a valid price', 'error');
        return;
      }
      value = parsed.value;
    }

    this.newItem.update((item) => {
      const next = { ...item, [field]: value };
      if (field === 'branch') {
        const stillValid = this.locations().some(
          (loc) =>
            loc.id === next.location &&
            (!loc.branchId || loc.branchId === String(value)),
        );
        if (!stillValid) {
          next.location = '';
        }
      }
      return next;
    });
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  clearFilters(): void {
    this.search.set('');
    this.categoryFilter.set('all');
    this.branchFilter.set(resetBranchFilterId(this.auth, this.branches()));
    this.locationFilter.set('all');
    this.statusFilter.set('all');
    this.rentableFilter.set('all');
    this.page.set(1);
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  openItemModal(): void {
    this.editingId.set(null);
    this.newItem.set({
      code: '',
      name: '',
      category: '',
      unit: '',
      branch: '',
      location: '',
      stockLevel: 0,
      reorderLevel: 0,
      price: 0,
      status: 'active',
      icon: 'bi-box-seam',
      isRentable: false,
    });
    this.showItemModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  openEditModal(item: InventoryItem): void {
    this.editingId.set(item.id);
    const categoryId =
      item.categoryId ??
      this.categories().find((row) => row.name === item.category)?.id ??
      item.category;
    const unitId =
      item.unitId ??
      this.units().find((row) => row.name === item.unit)?.id ??
      item.unit;
    const branchId =
      item.branchId ??
      this.branches().find((row) => row.name === item.branch)?.id ??
      item.branch ??
      '';
    const locationId =
      item.locationId ??
      this.locations().find((row) => row.name === item.location)?.id ??
      item.location ??
      '';

    this.newItem.set({
      code: item.code,
      name: item.name,
      category: categoryId,
      unit: unitId,
      branch: branchId,
      location: locationId,
      stockLevel: item.stockLevel,
      reorderLevel: item.reorderLevel,
      price: item.price,
      status: item.status,
      icon: item.icon ?? 'bi-box-seam',
      isRentable: item.isRentable ?? false,
    });
    this.showItemModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeItemModal(): void {
    this.showItemModal.set(false);
    this.editingId.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeItemModal();
    }
  }

  submitItem(): void {
    const item = this.newItem();
    const isEditing = !!this.editingId();

    const nameCheck = requiredText(item.name, 'Name');
    const categoryCheck = requiredText(item.category, 'Category');
    const unitCheck = requiredText(item.unit, 'Unit');
    const branchCheck = requiredText(item.branch, 'Branch');
    const locationCheck = requiredText(item.location, 'Location');
    if (
      !nameCheck.ok ||
      !categoryCheck.ok ||
      !unitCheck.ok ||
      !branchCheck.ok ||
      !locationCheck.ok
    ) {
      this.showToastMessage(
        nameCheck.message ??
          categoryCheck.message ??
          unitCheck.message ??
          branchCheck.message ??
          locationCheck.message ??
          'Please fill in all required fields',
        'error',
      );
      return;
    }

    const stockParsed = parseNonNegativeNumber(item.stockLevel, 'Stock level');
    const reorderParsed = parseNonNegativeNumber(
      item.reorderLevel,
      'Reorder level',
    );
    const priceParsed = parseNonNegativeNumber(item.price, 'Price');
    if (!stockParsed.ok) {
      this.showToastMessage(
        stockParsed.message ?? 'Stock level cannot be negative',
        'error',
      );
      return;
    }
    if (!reorderParsed.ok) {
      this.showToastMessage(
        reorderParsed.message ?? 'Reorder level cannot be negative',
        'error',
      );
      return;
    }
    if (!priceParsed.ok) {
      this.showToastMessage(
        priceParsed.message ?? 'Price cannot be negative',
        'error',
      );
      return;
    }

    if (!isEditing && !environment.useMockData) {
      // Item codes are generated by the API when adding new items.
    } else if (isEditing && !item.code) {
      this.showToastMessage('Item code is required', 'error');
      return;
    }

    const category = this.categories().find((row) => row.id === item.category);
    const unit = this.units().find((row) => row.id === item.unit);
    const branch = this.branches().find((row) => row.id === item.branch);
    const location = this.locations().find((row) => row.id === item.location);

    const payload = {
      code: item.code || 'AUTO',
      name: item.name,
      category: category?.name ?? item.category,
      categoryId: item.category,
      unit: unit?.name ?? item.unit,
      unitId: item.unit,
      branch: branch?.name ?? item.branch,
      branchId: item.branch,
      location: location?.name ?? item.location,
      locationId: item.location,
      stockLevel: stockParsed.value,
      reorderLevel: reorderParsed.value,
      price: priceParsed.value,
      status: item.status,
      icon: item.icon,
      isRentable: item.isRentable,
    };

    if (isEditing) {
      this.inventory
        .updateItemViaApi(this.editingId()!, payload)
        .subscribe((result) => {
          if (result.success) {
            this.showToastMessage('Item updated successfully', 'success');
            this.closeItemModal();
          } else {
            this.showToastMessage(
              result.message ?? 'Failed to update item',
              'error',
            );
          }
        });
      return;
    }

    this.inventory.createItemViaApi(payload).subscribe((result) => {
      if (result.success) {
        this.showToastMessage('Item added successfully', 'success');
        this.closeItemModal();
      } else {
        this.showToastMessage(result.message ?? 'Failed to add item', 'error');
      }
    });
  }

  deleteItem(item: InventoryItem): void {
    if (item.status === 'inactive' || this.deletingId()) {
      return;
    }

    if (
      !confirmAction(
        `Remove "${item.name}" (${item.code}) from the active catalog? It will be marked inactive. History is kept.`,
      )
    ) {
      return;
    }

    this.deletingId.set(item.id);
    this.inventory
      .deleteItemViaApi(item)
      .pipe(finalize(() => this.deletingId.set(null)))
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            'Item removed from the active catalog',
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to delete item',
            'error',
          );
        }
      });
  }

  exportData(): void {
    const rows = this.filteredItems().map((item) => ({
      code: item.code,
      name: item.name,
      category: item.category,
      unit: item.unit,
      branch: item.branch || 'N/A',
      location: item.location || 'N/A',
      stockLevel: item.stockLevel,
      reorderLevel: item.reorderLevel,
      price: item.price,
      status: item.status,
      isRentable: item.isRentable ? 'Yes' : 'No',
    }));

    if (!rows.length) {
      this.showToastMessage('No items match the current filters.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `item-master-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
